import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ArrowLeft, LayoutGrid, CalendarDays, FileText, Wrench, Receipt, FolderOpen, MessageSquare, ScrollText, Euro, Mail, Pencil, Trash2, Menu, HeartPulse } from "lucide-react";
import { HealthCheckPanel } from "@/components/HealthCheck";
import PhotoUploader from "@/components/PhotoUploader";
import Timeline from "@/components/Timeline";
import ReportForm from "@/components/ReportForm";
import QuoteBuilder from "@/components/QuoteBuilder";
import ContractBuilder from "@/components/ContractBuilder";
import { AddReport, DocsTab, InvoicesTab, IssuesTab, MessagesTab } from "@/components/propertyTabs";

const EVENT_TYPES = ["Visit", "Inspection", "Pool service", "Garden", "Cleaning", "Repair", "Storm callout", "Key handover", "Delivery", "Meter reading", "Note"];

const Field = ({ label, value }) => value ? (
  <div><div className="lbl-m">{label}</div><div style={{ fontSize: 14, marginTop: 2, wordBreak: "break-word", overflowWrap: "anywhere" }}>{value}</div></div>
) : null;

export default function PropertyProfile({ prop, onBack, reload }) {
  const [subtab, setSubtab] = useState("overview");
  const [menuOpen, setMenuOpen] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [edit, setEdit] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [autofill, setAutofill] = useState(false);
  const [d, setD] = useState({ reports: [], invoices: [], issues: [], documents: [], messages: [] });
  const pid = prop.property_id;

  const loadTimeline = useCallback(async () => {
    try { const { data } = await api.get(`/timeline?property_id=${pid}`); setTimeline(data); } catch (e) { /* */ }
  }, [pid]);
  const loadData = useCallback(async () => {
    try {
      const [r, i, is, dc, m] = await Promise.all([
        api.get(`/reports?property_id=${pid}`), api.get(`/invoices?property_id=${pid}`),
        api.get(`/issues?property_id=${pid}`), api.get(`/documents?property_id=${pid}`), api.get(`/messages?property_id=${pid}`)]);
      setD({ reports: r.data, invoices: i.data, issues: is.data, documents: dc.data, messages: m.data });
    } catch (e) { /* */ }
  }, [pid]);
  useEffect(() => { loadTimeline(); loadData(); }, [loadTimeline, loadData]);
  useEffect(() => { if (pid) api.post("/messages/read", { property_id: pid }).catch(() => {}); }, [pid]);

  const sendInvite = async () => {
    try { const { data } = await api.post(`/properties/${pid}/invite`); toast.success(data.already_active ? "This owner has already set their password." : `Portal invite sent to ${data.sent_to}`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const raiseInvoice = () => { setAutofill(true); setSubtab("invoices"); };
  const afterData = () => { loadData(); loadTimeline(); setRefreshKey((k) => k + 1); reload && reload(); };

  const TABS = [["overview", "Overview", <LayoutGrid size={16} />], ["visits", "Visits", <CalendarDays size={16} />], ["reports", "Reports", <FileText size={16} />], ["health", "Health check", <HeartPulse size={16} />], ["issues", "Issues", <Wrench size={16} />], ["invoices", "Invoices", <Receipt size={16} />], ["documents", "Documents", <FolderOpen size={16} />], ["messages", "Messages", <MessageSquare size={16} />], ["contracts", "Contracts", <ScrollText size={16} />], ["plan", "Plan & quote", <Euro size={16} />]];
  const openIssues = d.issues.filter((i) => i.status !== "resolved").length;
  const activeLabel = (TABS.find((t) => t[0] === subtab) || [])[1] || "";

  return (
    <div className="ws-shell" style={{ position: "fixed", inset: 0, zIndex: 80, background: "var(--paper)", display: "grid", gridTemplateColumns: "minmax(0,240px) 1fr" }} data-testid="property-profile-overlay">
      <div className="ws-topbar">
        <button className="ws-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Open property menu" data-testid="ws-menu-btn"><Menu size={16} /> Menu</button>
        <div style={{ minWidth: 0, overflow: "hidden" }}>
          <span className="lbl-m" style={{ color: "var(--honey)" }}>{prop.ref || "-"}</span>
          <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{prop.name}</div>
        </div>
      </div>
      {menuOpen && <div className="ws-overlay" onClick={() => setMenuOpen(false)} />}
      <aside className={`ws-aside ${menuOpen ? "open" : ""}`} style={{ borderRight: "1px solid var(--line)", background: "var(--paper)", padding: "24px 16px", position: "sticky", top: 0, height: "100vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>
        <button className="link-u" onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", marginLeft: 6 }} data-testid="profile-back-btn"><ArrowLeft size={15} /> All properties</button>
        <div style={{ margin: "18px 6px 22px" }} data-testid="property-profile">
          <span className="lbl-m" style={{ color: "var(--honey)" }} data-testid="profile-ref">{prop.ref || "-"}</span>
          <div style={{ fontSize: 19, fontWeight: 500, lineHeight: 1.2, marginTop: 2 }}>{prop.name}</div>
          <div className="lbl-m" style={{ marginTop: 3 }}>{prop.tier} plan</div>
        </div>
        <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {TABS.map(([k, lbl, icon]) => (
            <button key={k} className={`side-link ${subtab === k ? "active" : ""}`} onClick={() => { setSubtab(k); setMenuOpen(false); }} data-testid={`ws-tab-${k}`}>
              {icon}<span>{lbl}</span>{k === "issues" && openIssues ? <span className="nav-badge">{openIssues}</span> : null}
            </button>
          ))}
        </nav>
        <div style={{ marginTop: "auto", borderTop: "1px solid var(--line)", paddingTop: 14 }}>
          <button className="side-link" onClick={sendInvite} data-testid="send-invite-btn"><Mail size={16} /> Send portal invite</button>
        </div>
      </aside>

      <main style={{ padding: "clamp(20px,4vw,44px)", overflowY: "auto", height: "100vh", minWidth: 0 }}>
        <h2 style={{ fontSize: 26, marginBottom: 22 }}>{activeLabel}</h2>

      {subtab === "overview" && (
        <div style={{ display: "grid", gap: 22 }}>
          <div className="card-soft" style={{ overflow: "hidden" }}>
            {prop.cover_image && <img src={prop.cover_image} alt="" style={{ width: "100%", height: 180, objectFit: "cover" }} />}
            <div style={{ padding: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><div className="lbl">Property details</div><button className="btn-outline" onClick={() => setEdit((v) => !v)} data-testid="edit-details-btn">{edit ? "Close" : "Edit details"}</button></div>
              {!edit ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginTop: 18 }}>
                  <Field label="Owner" value={`${prop.owner_name} · ${prop.owner_email}`} />
                  <Field label="Owner phone" value={prop.owner_phone} />
                  <Field label="Address" value={prop.address} />
                  <Field label="Emergency contact" value={prop.emergency_contact} />
                  <Field label="Alarm code" value={prop.alarm_code} />
                  <Field label="Key location" value={prop.key_location} />
                  <Field label="Wi-Fi" value={prop.wifi} />
                  <Field label="Utilities" value={prop.utilities} />
                  <Field label="Access notes" value={prop.access_notes} />
                  <Field label="Matterport ID" value={prop.matterport_id} />
                  <Field label="Notes" value={prop.notes} />
                </div>
              ) : <EditDetails prop={prop} onSaved={() => { setEdit(false); reload(); }} />}
            </div>
          </div>
          <MonthPanel pid={pid} refreshKey={refreshKey} onRaiseInvoice={raiseInvoice} />
          <div className="card-soft" style={{ padding: 24, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="overview-health-card">
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <HeartPulse size={22} style={{ color: "var(--honey)", flexShrink: 0 }} />
              <div><div className="lbl">Health check</div><p style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 0", maxWidth: 460 }}>Run a free, non-intrusive property health check to win and onboard this owner. Flag anything a contractor should handle.</p></div>
            </div>
            <button className="btn-ink" onClick={() => setSubtab("health")} data-testid="overview-open-health-btn" style={{ flexShrink: 0 }}>Open health check</button>
          </div>
          <DigitalTwinPanel prop={prop} reload={reload} />
          <AssignContractorsPanel prop={prop} reload={reload} />
          <div><div className="lbl" style={{ marginBottom: 16 }}>Timeline</div><Timeline items={timeline} canManage onChanged={afterData} /></div>
        </div>
      )}
      {subtab === "visits" && (
        <div style={{ display: "grid", gap: 22 }}>
          <VisitsCalendar pid={pid} onChanged={afterData} />
          <NewInspection pid={pid} onDone={afterData} />
          <LogEvent pid={pid} prop={prop} onLogged={afterData} />
          <div><div className="lbl" style={{ marginBottom: 16 }}>All visits & events</div><Timeline items={timeline} canManage onChanged={afterData} /></div>
        </div>
      )}
      {subtab === "reports" && <AddReport pid={pid} reports={d.reports} reload={afterData} />}
      {subtab === "health" && <HealthCheckPanel pid={pid} reload={afterData} />}
      {subtab === "issues" && <IssuesTab issues={d.issues} reload={afterData} />}
      {subtab === "invoices" && <InvoicesTab pid={pid} invoices={d.invoices} reload={afterData} autofill={autofill} onAutofilled={() => setAutofill(false)} />}
      {subtab === "documents" && <DocsTab pid={pid} documents={d.documents} reload={afterData} />}
      {subtab === "messages" && <MessagesTab pid={pid} messages={d.messages} reload={afterData} />}
      {subtab === "contracts" && <ContractPanel prop={prop} />}
      {subtab === "plan" && <div style={{ display: "grid", gap: 22 }}><QuotePanel prop={prop} reload={() => { reload && reload(); setRefreshKey((k) => k + 1); }} /><ChecklistTemplate prop={prop} reload={reload} /></div>}
      </main>
    </div>
  );
}

const extractMpId = (s) => {
  const t = (s || "").trim();
  if (!t) return "";
  const m = t.match(/[?&]m=([^&\s]+)/);
  if (m) return m[1];
  return t.replace(/^https?:\/\/\S*\//, "");
};

function DigitalTwinPanel({ prop, reload }) {
  const [val, setVal] = useState(prop.matterport_id || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const id = extractMpId(val);
    setBusy(true);
    try { await api.put(`/properties/${prop.property_id}`, { matterport_id: id }); toast.success(id ? "Digital twin linked · now visible in the owner's portal" : "Digital twin cleared"); setVal(id); reload && reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const id = extractMpId(val);
  return (
    <div className="card-soft" style={{ padding: 24 }} data-testid="digital-twin-panel">
      <div className="lbl">Digital twin · Matterport</div>
      <p style={{ fontSize: 13, color: "var(--muted)", margin: "6px 0 14px" }}>Paste the Matterport share link or model ID. Once saved, it appears on the owner's portal under Digital twin.</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input className="inp" style={{ flex: 1, minWidth: 260 }} placeholder="https://my.matterport.com/show/?m=XXXXXXXXXX  or  XXXXXXXXXX" value={val} onChange={(e) => setVal(e.target.value)} data-testid="matterport-input" />
        <button className="btn-ink" onClick={save} disabled={busy} data-testid="matterport-save-btn">{busy ? "Saving…" : "Save"}</button>
      </div>
      {id ? (
        <div style={{ position: "relative", paddingTop: "56.25%", marginTop: 16, borderRadius: 8, overflow: "hidden", border: "1px solid var(--line)" }} data-testid="matterport-preview">
          <iframe title="Matterport preview" src={`https://my.matterport.com/show/?m=${id}&play=1`} frameBorder="0" allowFullScreen allow="xr-spatial-tracking" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
        </div>
      ) : <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 12 }}>No twin linked yet. The owner sees a "coming soon" note until you add one.</p>}
    </div>
  );
}

function AssignContractorsPanel({ prop, reload }) {
  const [all, setAll] = useState([]);
  const [ids, setIds] = useState(prop.assigned_contractor_ids || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/contractors").then(({ data }) => setAll(data.filter((c) => c.active !== false))).catch(() => {}); }, []);
  const toggle = (cid) => setIds((cur) => cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid]);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/properties/${prop.property_id}`, { assigned_contractor_ids: ids }); toast.success("Contractor access updated"); reload && reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const dirty = JSON.stringify([...(prop.assigned_contractor_ids || [])].sort()) !== JSON.stringify([...ids].sort());
  return (
    <div className="card-soft" style={{ padding: 24 }} data-testid="assign-contractors-panel">
      <div className="lbl">Contractor access</div>
      <p style={{ fontSize: 13, color: "var(--muted)", margin: "6px 0 14px" }}>Give trusted contractors access to this villa. They'll see it in their portal with the digital twin and their assigned jobs — nothing else.</p>
      {all.length === 0 ? <p style={{ fontSize: 13, color: "var(--muted)" }}>No contractors yet. Add them under Contractors.</p> : (
        <div style={{ display: "grid", gap: 8 }}>
          {all.map((c) => {
            const on = ids.includes(c.contractor_id);
            return (
              <label key={c.contractor_id} data-testid={`assign-contractor-${c.contractor_id}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8, cursor: "pointer", background: on ? "var(--soft)" : "var(--paper)" }}>
                <input type="checkbox" checked={on} onChange={() => toggle(c.contractor_id)} data-testid={`assign-toggle-${c.contractor_id}`} style={{ width: 16, height: 16 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{c.name}{c.company ? ` · ${c.company}` : ""}</div>
                  <div className="lbl-m" style={{ marginTop: 2 }}>{c.trade || "Contractor"}{c.has_login ? "" : " · no portal login yet"}</div>
                </div>
              </label>
            );
          })}
        </div>
      )}
      <button className="btn-ink" style={{ marginTop: 16 }} onClick={save} disabled={busy || !dirty} data-testid="assign-contractors-save">{busy ? "Saving…" : "Save access"}</button>
    </div>
  );
}

function NewInspection({ pid, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-soft" style={{ padding: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="lbl">Inspection report</div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>Record a detailed, area-by-area inspection of what's good and what needs attention.</div>
        </div>
        <button className="btn-ink" onClick={() => setOpen((o) => !o)} data-testid="new-inspection-btn">{open ? "Close" : "＋ New inspection"}</button>
      </div>
      {open && <div style={{ marginTop: 18 }}><ReportForm pid={pid} reload={() => { onDone(); }} /></div>}
    </div>
  );
}

const CAL_WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CAL_TYPES = ["Inspection", "Pool service", "Cleaning", "Garden", "Repair", "Owner arrival", "Meeting", "Delivery", "Meter reading", "Other"];

const KIND_DOT = { appointment: "#c9a227", job: "#b06a1f", report: "#5B6440", event: "#3a5a6b" };
const KIND_LEGEND = [["appointment", "Visit"], ["job", "Contractor"], ["report", "Inspection"], ["event", "Event"]];

function VisitsCalendar({ pid, onChanged }) {
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({ date: "", time: "10:00", type: "Inspection", title: "", staff_id: "" });
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const load = useCallback(() => {
    const url = `/properties/${pid}/month-summary${month ? `?month=${month}` : ""}`;
    api.get(url).then(({ data }) => { setData(data); if (!month) setMonth(data.month); }).catch(() => {});
  }, [pid, month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/staff-members").then(({ data }) => { setStaff(data); setF((s) => ({ ...s, staff_id: s.staff_id || data[0]?.user_id || "" })); }).catch(() => {}); }, []);
  if (!data) return null;
  const [y, m] = data.month.split("-").map(Number);
  const startCol = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const daysIn = new Date(y, m, 0).getDate();
  const byDay = {};
  (data.visits || []).forEach((v) => { const d = (v.date || "").slice(0, 10); if (d) (byDay[d] = byDay[d] || []).push(v); });
  const shift = (delta) => { const d = new Date(y, m - 1 + delta, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); };
  const dstr = (d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const cells = [];
  for (let i = 0; i < startCol; i++) cells.push(null);
  for (let d = 1; d <= daysIn; d++) cells.push(d);
  const book = async (e) => {
    e.preventDefault();
    if (!f.date) return toast.error("Pick a day on the calendar first");
    if (!f.staff_id) return toast.error("No staff member to assign");
    const start = new Date(`${f.date}T${f.time}`);
    if (isNaN(start.getTime())) return toast.error("Pick a valid time");
    setBusy(true);
    const payload = { property_id: pid, staff_id: f.staff_id, type: f.type, title: f.title.trim() || f.type, start: start.toISOString(), end: new Date(start.getTime() + 3600000).toISOString() };
    try {
      if (editId) { await api.patch(`/appointments/${editId}?force=true`, payload); toast.success("Visit updated"); }
      else { await api.post("/appointments?force=true", payload); toast.success("Visit booked"); }
      setF({ ...f, title: "", type: "Inspection", time: "10:00" }); setEditId(null); load(); onChanged && onChanged();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  const editVisit = (v) => {
    const dt = new Date(v.date);
    setEditId(v.id);
    setF({ date: (v.date || "").slice(0, 10), time: isNaN(dt.getTime()) ? "10:00" : `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`, type: v.type || "Inspection", title: v.title && v.title !== v.type ? v.title : "", staff_id: v.staff_id || staff[0]?.user_id || "" });
  };
  const delVisit = async (v) => {
    if (!window.confirm("Remove this visit from the calendar?")) return;
    try { await api.delete(`/appointments/${v.id}`); toast.success("Visit removed"); if (editId === v.id) { setEditId(null); setF({ ...f, title: "" }); } load(); onChanged && onChanged(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="card-soft" style={{ padding: 18, maxWidth: 680 }} data-testid="visits-calendar">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div className="lbl">Visit calendar</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="cal-nav" onClick={() => shift(-1)} data-testid="vc-prev" style={{ padding: "3px 10px", cursor: "pointer" }}>‹</button>
          <span className="lbl-m" data-testid="vc-month">{data.month_label}</span>
          <button className="cal-nav" onClick={() => shift(1)} data-testid="vc-next" style={{ padding: "3px 10px", cursor: "pointer" }}>›</button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
        {CAL_WD.map((w) => <div key={w} className="lbl-m" style={{ textAlign: "center", fontSize: 11, padding: "2px 0" }}>{w}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const ds = dstr(d);
          const vs = byDay[ds] || [];
          const sel = f.date === ds;
          return (
            <button key={i} type="button" onClick={() => setF({ ...f, date: ds })} data-testid={vs.length ? "vc-day-has-visits" : "vc-day"} title={vs.map((v) => `${v.type}: ${v.title || ""}`).join("\n")} style={{ minHeight: 56, border: sel ? "2px solid var(--ink)" : "1px solid var(--line)", borderRadius: 6, background: sel ? "var(--soft)" : "var(--paper)", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start", gap: 2, padding: "3px 3px 2px", textAlign: "left", overflow: "hidden" }}>
              <span style={{ fontSize: 11, fontWeight: 500, color: sel ? "var(--ink)" : "var(--muted)", paddingLeft: 2 }}>{d}</span>
              {vs.slice(0, 2).map((v, k) => (
                <span key={k} style={{ fontSize: 9, lineHeight: 1.25, background: v.type === "Storm callout" ? "#3a5a99" : (KIND_DOT[v.kind] || "var(--honey)"), color: "#fff", borderRadius: 3, padding: "1px 3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>{v.title || v.type}</span>
              ))}
              {vs.length > 2 && <span style={{ fontSize: 8.5, color: "var(--muted)", paddingLeft: 2 }}>+{vs.length - 2} more</span>}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10 }} data-testid="vc-legend">
        {KIND_LEGEND.map(([k, lbl]) => (
          <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--muted)" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: KIND_DOT[k] }} />{lbl}
          </span>
        ))}
      </div>
      {f.date && (
        <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 12 }} data-testid="vc-day-detail">
          <div className="lbl-m" style={{ marginBottom: 8 }}>{new Date(`${f.date}T00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "2-digit", year: "2-digit" })}</div>
          {(byDay[f.date] || []).length === 0
            ? <p style={{ fontSize: 13, color: "var(--muted)" }}>No visits on this day yet. Book one below.</p>
            : <div style={{ display: "grid", gap: 6 }}>
                {(byDay[f.date] || []).map((v, k) => {
                  const t = new Date(v.date);
                  const tm = isNaN(t.getTime()) ? "" : t.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                  const canEdit = v.kind === "appointment" && v.id;
                  return (
                    <div key={k} data-testid="vc-visit-item" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "7px 10px", background: editId === v.id ? "var(--soft)" : "var(--paper)", border: "1px solid var(--line)", borderRadius: 8 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--honey)", flexShrink: 0 }} />
                      {tm && <span style={{ fontVariantNumeric: "tabular-nums", color: "var(--muted)", minWidth: 42 }}>{tm}</span>}
                      <span style={{ fontWeight: 500, color: v.type === "Storm callout" ? "#3a5a99" : (KIND_DOT[v.kind] || "var(--ink)") }}>{v.type}</span>
                      {v.title && v.title !== v.type && <span style={{ color: "var(--muted)" }}>· {v.title}</span>}
                      {v.cost != null && <span className="pill-tag" style={{ background: "#f2e8dc", color: "#8a5a1f", marginLeft: 6 }}>€{Number(v.cost).toFixed(2)}</span>}
                      {v.who && <span style={{ color: "var(--muted)", marginLeft: canEdit ? 0 : "auto" }}>{v.who}</span>}
                      {canEdit && (
                        <span style={{ marginLeft: "auto", display: "flex", gap: 4, flexShrink: 0 }}>
                          <button type="button" className="cal-nav" onClick={() => editVisit(v)} aria-label="Edit visit" data-testid="vc-edit-visit" style={{ padding: 4, cursor: "pointer" }}><Pencil size={14} /></button>
                          <button type="button" className="cal-nav" onClick={() => delVisit(v)} aria-label="Remove visit" data-testid="vc-delete-visit" style={{ padding: 4, cursor: "pointer" }}><Trash2 size={14} /></button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>}
        </div>
      )}
      <form onSubmit={book} style={{ marginTop: 16, display: "grid", gap: 10, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
        <div className="lbl-m">{editId ? "Edit visit" : "Book a visit"} {f.date ? `· ${f.date}` : "· pick a day above"}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input className="inp" type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} style={{ maxWidth: 120 }} data-testid="vc-time" />
          <select className="inp" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} style={{ maxWidth: 170 }} data-testid="vc-type">{CAL_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          <select className="inp" value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value })} style={{ maxWidth: 180 }} data-testid="vc-staff">{staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}</select>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input className="inp" placeholder="Note (optional)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} style={{ flex: 1 }} data-testid="vc-note" />
          {editId && <button className="btn-outline" type="button" onClick={() => { setEditId(null); setF({ ...f, title: "", type: "Inspection", time: "10:00" }); }} data-testid="vc-cancel-edit">Cancel</button>}
          <button className="btn-ink" type="submit" disabled={busy} data-testid="vc-book-btn">{busy ? "Saving…" : editId ? "Save changes" : "Book visit"}</button>
        </div>
      </form>
    </div>
  );
}

function MonthPanel({ pid, refreshKey, onRaiseInvoice }) {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  useEffect(() => {
    const url = `/properties/${pid}/month-summary${month ? `?month=${month}` : ""}`;
    api.get(url).then(({ data }) => { setData(data); if (!month) setMonth(data.month); }).catch(() => {});
  }, [pid, month, refreshKey]);
  const shiftMonth = (ym, delta) => { const [y, m] = ym.split("-").map(Number); const d = new Date(y, m - 1 + delta, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
  if (!data) return null;
  const fmtD = (d) => { const dt = new Date(d); return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "2-digit", year: "2-digit" }); };
  const money = (n) => `€${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
  const KIND_LABEL = { appointment: "#3a5170", job: "#8a5a1f", report: "#5B6440", event: "#3a5a6b" };
  return (
    <div className="card-soft" style={{ padding: 22 }} data-testid="month-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="cal-nav" onClick={() => setMonth(shiftMonth(data.month, -1))} data-testid="month-prev-btn" aria-label="Previous month" style={{ padding: "3px 10px", cursor: "pointer" }}>‹</button>
          <div className="lbl" data-testid="month-label">{data.month_label}</div>
          <button className="cal-nav" onClick={() => setMonth(shiftMonth(data.month, 1))} data-testid="month-next-btn" aria-label="Next month" style={{ padding: "3px 10px", cursor: "pointer" }}>›</button>
        </div>
        <div style={{ fontSize: 13, color: "var(--muted)" }}>{data.plan.tier || "No tier"} · {data.plan.monthly_amount ? `${money(data.plan.monthly_amount)}/mo` : "no monthly fee set"} · {data.plan.visits_per_month} paid visit(s)/mo{data.plan.paused ? " · paused" : ""}</div>
      </div>
      <div style={{ display: "flex", gap: 20, margin: "14px 0", flexWrap: "wrap" }}>
        <Stat label="Visits done" value={data.totals.done} />
        <Stat label="Scheduled" value={data.totals.scheduled} />
        <Stat label="Extra costs" value={money(data.totals.extra_cost)} />
      </div>
      {data.visits.length === 0
        ? <p style={{ fontSize: 13, color: "var(--muted)" }}>No visits logged or scheduled this month yet.</p>
        : <div style={{ display: "grid", gap: 6 }} data-testid="month-visits">
            {data.visits.map((v, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, fontSize: 13, borderBottom: "1px solid var(--line)", padding: "6px 0" }} data-testid="month-visit-row">
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 500 }}>{fmtD(v.date)}</span>
                  <span className="lbl-m" style={{ color: v.type === "Storm callout" ? "#3a5a99" : (KIND_LABEL[v.kind] || "var(--muted)"), marginLeft: 8 }}>{v.type}</span>
                  <span style={{ marginLeft: 8, color: "var(--muted)" }}>{v.title}{v.who ? ` · ${v.who}` : ""}</span>
                </div>
                <span style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                  {v.cost != null && <span className="pill-tag" style={{ background: "#ece2f3", color: "#6b4e8a" }}>{money(v.cost)}</span>}
                  {v.status && <span className="pill-tag" style={{ background: v.status === "completed" ? "#e7ecdf" : "var(--sand)", color: v.status === "completed" ? "#5B6440" : "var(--umber)" }}>{v.status === "completed" ? "Done" : v.status}</span>}
                </span>
              </div>
            ))}
          </div>}
      <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 10, flexWrap: "wrap" }}>
          <div className="lbl-m">Invoices for {data.month_label}</div>
          {onRaiseInvoice && <button className="btn-ink" style={{ padding: "5px 13px", fontSize: 12 }} onClick={onRaiseInvoice} data-testid="raise-invoice-btn">Raise invoice</button>}
        </div>
        {data.invoices.length === 0
          ? <p style={{ fontSize: 13, color: "var(--muted)" }}>No invoice raised for this month yet. Use the Invoices tab → “Auto-fill from plan” to create it (the monthly fee and any costed visits above are pulled in automatically).</p>
          : data.invoices.map((inv) => (
              <div key={inv.invoice_id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
                <span>{inv.description}</span>
                <span><b>€{Number(inv.amount).toFixed(2)}</b> <span className="pill-tag" style={{ background: inv.status === "paid" ? "#e7ecdf" : "var(--sand)", color: inv.status === "paid" ? "#5B6440" : "var(--umber)", marginLeft: 6 }}>{inv.status}</span></span>
              </div>
            ))}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div><div className="lbl-m">{label}</div><div className="font-serif-c" style={{ fontSize: 24 }}>{value}</div></div>
);

function QuotePanel({ prop, reload }) {
  const [open, setOpen] = useState(false);
  const [visits, setVisits] = useState(prop.plan?.visits_per_month ?? "");
  const [fee, setFee] = useState(prop.plan?.monthly_amount ?? "");
  const [quotes, setQuotes] = useState([]);
  const [qtab, setQtab] = useState("active");
  const loadQuotes = useCallback(() => { api.get(`/quotes?property_id=${prop.property_id}`).then(({ data }) => setQuotes(data)).catch(() => {}); }, [prop.property_id]);
  useEffect(() => { loadQuotes(); }, [loadQuotes, open]);
  const followUp = async (id) => { try { await api.post(`/quotes/${id}/follow-up`); toast.success("Follow-up sent · moved to history"); loadQuotes(); } catch (e) { toast.error(apiErr(e)); } };
  const plan = prop.plan;
  const paused = !!plan?.paused;
  const savePlan = async () => { try { await api.patch(`/properties/${prop.property_id}/plan`, { visits_per_month: parseInt(visits) || 0, monthly_amount: parseFloat(fee) || 0 }); toast.success("Plan updated"); reload && reload(); } catch (e) { toast.error(apiErr(e)); } };
  const togglePause = async () => { try { await api.patch(`/properties/${prop.property_id}/plan`, { paused: !paused }); toast.success(!paused ? "Plan paused" : "Plan resumed"); reload && reload(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="card-soft" style={{ padding: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="lbl">Service plan & quote {paused && <span className="pill-tag" style={{ background: "var(--sand)", color: "var(--umber)", marginLeft: 8 }} data-testid="plan-paused-badge">Plan paused</span>}</div>
          {plan ? <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{plan.tier} · {plan.visits_per_month} visits/mo · €{plan.monthly_amount || 0}/mo</div>
            : <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>No active plan yet, set a monthly fee below or build a quote to send to the owner.</div>}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
            <span className="lbl-m">Paid visits / month</span>
            <input className="inp" type="number" style={{ width: 80 }} value={visits} onChange={(e) => setVisits(e.target.value)} data-testid="plan-visits-input" />
            <span className="lbl-m">Monthly fee €</span>
            <input className="inp" type="number" step="0.01" style={{ width: 100 }} value={fee} onChange={(e) => setFee(e.target.value)} data-testid="plan-fee-input" placeholder="0.00" />
            <button className="btn-outline" style={{ padding: "6px 12px" }} onClick={savePlan} data-testid="save-visits-btn">Save</button>
            {plan && <button className="btn-outline" style={{ padding: "6px 12px" }} onClick={togglePause} data-testid="toggle-pause-btn">{paused ? "Resume plan" : "Pause plan"}</button>}
          </div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>The monthly fee is what appears on the invoice each month. Set it here to include this property in the monthly invoice run.</div>
        </div>
        <button className="btn-ink" onClick={() => setOpen(true)} data-testid="create-quote-btn">Create quote</button>
      </div>
      {plan?.line_items?.length ? <div style={{ marginTop: 12, fontSize: 13 }}>{plan.line_items.map((l, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--line)", padding: "5px 0" }}><span>{l.label}{l.qty > 1 ? ` ×${l.qty}` : ""}</span><span>€{l.amount}</span></div>)}</div> : null}
      {quotes.length > 0 && (
        <div style={{ marginTop: 18, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {["active", "history"].map((k) => { const n = quotes.filter((q) => k === "active" ? (!q.followed_up && q.status === "sent") : (q.followed_up || q.status !== "sent")).length; return <button key={k} className={qtab === k ? "btn-ink" : "btn-outline"} style={{ padding: "5px 14px", fontSize: 13, textTransform: "capitalize" }} onClick={() => setQtab(k)} data-testid={`quote-tab-${k}`}>{k} ({n})</button>; })}
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {quotes.filter((q) => qtab === "active" ? (!q.followed_up && q.status === "sent") : (q.followed_up || q.status !== "sent")).map((q) => (
              <div key={q.quote_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, border: "1px solid var(--line)", borderRadius: 8, padding: "10px 12px" }} data-testid="quote-row">
                <div style={{ minWidth: 0 }}>
                  <b style={{ fontSize: 14 }}>{q.tier} · €{Number(q.total || 0).toFixed(2)}/mo</b>
                  <div className="lbl-m" style={{ color: "var(--muted)" }}>{new Date(q.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" })} · <span style={{ textTransform: "capitalize" }}>{q.status}</span>{q.follow_up_count ? ` · followed up ${q.follow_up_count}×` : ""}</div>
                </div>
                {qtab === "active" && q.status === "sent" && <button className="btn-outline" title="Emails the owner a reminder about this pending quote and logs the follow-up" style={{ padding: "5px 12px", fontSize: 12, flexShrink: 0 }} onClick={() => followUp(q.quote_id)} data-testid="follow-up-btn">Follow up</button>}
                {q.status === "accepted" && <span className="pill-tag" style={{ background: "#e7ecdf", color: "#5B6440" }}>Accepted</span>}
              </div>
            ))}
            {quotes.filter((q) => qtab === "active" ? (!q.followed_up && q.status === "sent") : (q.followed_up || q.status !== "sent")).length === 0 && <p style={{ fontSize: 13, color: "var(--muted)" }}>Nothing here.</p>}
          </div>
        </div>
      )}
      {open && <QuoteBuilder prop={prop} onClose={() => setOpen(false)} onSaved={() => reload && reload()} />}
    </div>
  );
}

function ContractPanel({ prop }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get(`/contracts?property_id=${prop.property_id}`).then(({ data }) => setList(data)).catch(() => {}), [prop.property_id]);
  useEffect(() => { load(); }, [load]);
  const send = async (id) => { try { await api.post(`/contracts/${id}/send`); toast.success("Contract emailed to client"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { try { await api.delete(`/contracts/${id}`); toast.success("Contract deleted"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const BE = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="card-soft" style={{ padding: 22 }} data-testid="contract-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="lbl">Service agreement</div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>Build a signable contract from the standard terms plus the services you're providing.</div>
        </div>
        <button className="btn-ink" onClick={() => setOpen(true)} data-testid="create-contract-btn">Create contract</button>
      </div>
      {list.length > 0 && (
        <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
          {list.map((c) => (
            <div key={c.contract_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", borderTop: "1px solid var(--line)", paddingTop: 10 }} data-testid="contract-row">
              <div style={{ fontSize: 14 }}><span className="lbl-m" style={{ color: "var(--honey)" }}>{c.number}</span> · {c.tier} · €{((c.monthly_fee || 0) + (c.services || []).reduce((s, x) => s + (/month/i.test(x.frequency || "") ? (x.amount || 0) : 0), 0)).toFixed(0)}/mo <span className="pill-tag" style={{ background: "var(--sand)", color: "var(--umber)", marginLeft: 6 }}>{c.status}</span></div>
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <a className="link-u" style={{ fontSize: 11 }} href={`${BE}/api/contracts/${c.contract_id}/pdf`} target="_blank" rel="noreferrer" data-testid="contract-pdf-btn">PDF (EN)</a>
                <a className="link-u" style={{ fontSize: 11 }} href={`${BE}/api/contracts/${c.contract_id}/pdf?bilingual=es`} target="_blank" rel="noreferrer" data-testid="contract-pdf-en-es">EN+ES</a>
                <a className="link-u" style={{ fontSize: 11 }} href={`${BE}/api/contracts/${c.contract_id}/pdf?bilingual=de`} target="_blank" rel="noreferrer" data-testid="contract-pdf-en-de">EN+DE</a>
                <button className="link-u" style={{ fontSize: 11, background: "none", border: "none", cursor: "pointer" }} onClick={() => send(c.contract_id)} data-testid="contract-send-btn">Send</button>
                <button className="link-u" style={{ fontSize: 11, background: "none", border: "none", cursor: "pointer", color: "var(--muted)" }} onClick={() => del(c.contract_id)} data-testid="contract-delete-btn">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {open && <ContractBuilder prop={prop} onClose={() => setOpen(false)} onSaved={load} />}
    </div>
  );
}

function EditDetails({ prop, onSaved }) {
  const FIELDS = [["name", "Property name"], ["location", "Location"], ["tier", "Tier"], ["owner_name", "Owner name"], ["owner_email", "Owner email"], ["address", "Address"], ["owner_phone", "Owner phone"], ["emergency_contact", "Emergency contact"], ["alarm_code", "Alarm code"], ["key_location", "Key location"], ["wifi", "Wi-Fi"], ["utilities", "Utilities"], ["matterport_id", "Matterport ID"], ["cover_image", "Cover image URL"]];
  const [f, setF] = useState(() => {
    const base = Object.fromEntries(FIELDS.map(([k]) => [k, prop[k] || ""]));
    return { ...base, access_notes: prop.access_notes || "", notes: prop.notes || "" };
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/properties/${prop.property_id}`, { ...f, storm_callout_fee: storm === "" ? null : parseFloat(storm) }); toast.success("Details updated"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const [storm, setStorm] = useState(prop.storm_callout_fee != null ? String(prop.storm_callout_fee) : "");
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12, marginTop: 20 }}>
      {FIELDS.map(([k, label]) => <input key={k} className="inp" placeholder={label} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`field-${k}`} />)}
      <input className="inp" type="number" step="0.01" placeholder="Storm callout default fee €" value={storm} onChange={(e) => setStorm(e.target.value)} data-testid="storm-fee-input" />
      <textarea className="inp" style={{ gridColumn: "1/-1" }} placeholder="Access notes" value={f.access_notes} onChange={(e) => setF({ ...f, access_notes: e.target.value })} data-testid="field-access_notes" />
      <textarea className="inp" style={{ gridColumn: "1/-1" }} placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="field-notes" />
      <p className="lbl-m" style={{ gridColumn: "1/-1", color: "var(--muted)", margin: 0, lineHeight: 1.5 }}>Changing the owner email links this villa to that owner's portal login. For a brand-new email, use “Send portal invite” afterwards so they can set a password.</p>
      <button className="btn-ink" onClick={save} disabled={saving} style={{ justifySelf: "start" }} data-testid="save-details-btn">{saving ? "Saving…" : "Save details"}</button>
    </div>
  );
}

function ChecklistTemplate({ prop, reload }) {
  const [text, setText] = useState((prop.checklist_template || []).join("\n"));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const items = text.split("\n").map((s) => s.trim()).filter(Boolean);
    try { await api.put(`/properties/${prop.property_id}`, { checklist_template: items }); toast.success("Checklist template saved"); reload && reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <div className="card-soft" style={{ padding: 22 }} data-testid="checklist-template-panel">
      <div className="lbl">Pre-arrival checklist template</div>
      <div style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 12px" }}>One item per line. Used for every new trip at this property. Leave blank to use the standard list.</div>
      <textarea className="inp" rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={"Fridge & pantry stocked\nStart pool heating\nFeed the cat"} data-testid="checklist-template-input" />
      <button className="btn-ink" onClick={save} disabled={saving} style={{ marginTop: 12 }} data-testid="save-checklist-template-btn">{saving ? "Saving…" : "Save template"}</button>
    </div>
  );
}

function LogEvent({ pid, prop, onLogged }) {
  const today = new Date().toISOString().slice(0, 10);
  const stormFee = prop && prop.storm_callout_fee != null ? prop.storm_callout_fee : "";
  const [f, setF] = useState({ date: today, type: "Visit", note: "", cost: "" });
  const [photos, setPhotos] = useState([]);
  const setType = (type) => {
    setF((s) => {
      const next = { ...s, type };
      if (type === "Storm callout" && !s.cost && stormFee !== "") next.cost = String(stormFee);
      return next;
    });
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/events", { property_id: pid, date: f.date, type: f.type, note: f.note, cost: f.cost ? parseFloat(f.cost) : null, photos });
      toast.success(f.type === "Storm callout" ? "Storm callout logged · added to timeline & this month's invoice extras" : "Event added to timeline");
      setF({ date: today, type: "Visit", note: "", cost: "" }); setPhotos([]); onLogged();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <form onSubmit={submit} className="card-soft" style={{ padding: 22, display: "grid", gap: 12 }} data-testid="log-event-form">
      <div className="lbl">Log a check or event</div>
      {f.type === "Storm callout" && <p style={{ fontSize: 12, color: "#6b4e8a", margin: "-2px 0 0" }}>Storm/emergency weather visit. The charge below is billed as an extra on this month's invoice.{stormFee !== "" ? ` Default €${stormFee} — edit if this callout differs.` : " Set a default under Edit details to auto-fill this."}</p>}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <input className="inp" type="date" style={{ maxWidth: 180 }} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} required data-testid="event-date-input" />
        <select className="inp" style={{ maxWidth: 200 }} value={f.type} onChange={(e) => setType(e.target.value)} data-testid="event-type-select">{EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        <input className="inp" style={{ maxWidth: 150 }} type="number" step="0.01" placeholder="Cost € (optional)" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} data-testid="event-cost-input" />
      </div>
      <textarea className="inp" rows={2} placeholder={f.type === "Storm callout" ? "What happened / what did you check or secure (shutters, drains, furniture)" : "What was done / observed"} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} required data-testid="event-note-input" />
      <PhotoUploader value={photos} onChange={setPhotos} label="Photos (optional)" withAreas />
      <button className="btn-ink" type="submit" style={{ justifySelf: "start" }} data-testid="log-event-btn">Add to timeline</button>
    </form>
  );
}
